import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/redux/hooks";
import { fetchEvaluationById } from "@/components/history/historySlice";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Download,
  ArrowLeft,
  AlertCircle,
  LayoutDashboard,
  ChevronRight,
  MoreVertical,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";

interface Question {
  id: number;
  question: string;
  answer: string;
  feedback: string;
  score: number;
}

const Results = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const evaluationId = searchParams.get("evaluation_id");
  const [evaluationData, setEvaluationData] = useState<{
    fileName: string;
    date: string;
    duration: string;
    feedback: Question[];
  } | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [activeSection, setActiveSection] = useState("overall");
  const [expandedAnswers, setExpandedAnswers] = useState<Set<number>>(new Set());
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Fetch evaluation data
  useEffect(() => {
    if (evaluationId) {
      setIsFetching(true);
      setIsError(false);
      setErrorMessage("");
      dispatch(fetchEvaluationById(evaluationId))
        .then((action: any) => {
          if (fetchEvaluationById.fulfilled.match(action)) {
            const data = action.payload;
            setEvaluationData({
              fileName: data.file_name || data.fileName || "Evaluation",
              date: data.date || new Date().toLocaleDateString(),
              duration: data.duration || "-",
              feedback: data.feedback || data.questions || [],
            });
            setIsFetching(false);
          } else {
            setIsError(true);
            setIsFetching(false);
            setErrorMessage(action.payload?.message || "Failed to load evaluation results");
            toast.error(action.payload?.message || "Failed to load evaluation results");
          }
        })
        .catch(() => {
          setIsError(true);
          setIsFetching(false);
          setErrorMessage("Failed to load evaluation results");
          toast.error("Failed to load evaluation results");
        });
    } else {
      toast.error("No evaluation ID provided");
      // Don't navigate immediately, show the error message first
    }
  }, [evaluationId, dispatch]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  if (!evaluationId) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8 flex items-center justify-center">
        <Card className="p-12 text-center">
          <h2 className="text-2xl font-bold mb-4">Evaluation ID Required</h2>
          <p className="text-muted-foreground mb-6">
            Please provide an evaluation ID to view results.
          </p>
          <Button onClick={() => navigate("/history")}>Back to History</Button>
        </Card>
      </div>
    );
  }

  if (isFetching && !evaluationData) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8">
        <div className="container max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-12 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (isError || !evaluationData) {
    return (
      <div className="min-h-screen bg-background p-4 md:p-8 flex items-center justify-center">
        <Card className="p-12 text-center">
          <h2 className="text-2xl font-bold mb-4">Evaluation Not Found</h2>
          <p className="text-muted-foreground mb-6">
            {errorMessage || "The evaluation you're looking for doesn't exist."}
          </p>
          <Button onClick={() => navigate("/history")}>Back to History</Button>
        </Card>
      </div>
    );
  }

  const totalScore = evaluationData.feedback.reduce(
    (acc, q) => acc + q.score,
    0
  );
  const maxScore = evaluationData.feedback.length * 10;
  const percentage = (totalScore / maxScore) * 100;

  const getScoreColor = (score: number) => {
    if (score >= 8) return "text-emerald-600 bg-emerald-50 border-emerald-100";
    if (score >= 5) return "text-amber-600 bg-amber-50 border-amber-100";
    return "text-rose-600 bg-rose-50 border-rose-100";
  };

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      const getScrollableParent = (
        node: HTMLElement | null
      ): HTMLElement | Window => {
        let el: HTMLElement | null = node;
        while (el && el !== document.body) {
          const style = getComputedStyle(el);
          const overflowY = style.overflowY;
          if (
            (overflowY === "auto" || overflowY === "scroll") &&
            el.scrollHeight > el.clientHeight
          ) {
            return el;
          }
          el = el.parentElement;
        }
        return window;
      };

      const scrollParent = getScrollableParent(element as HTMLElement);
      const headerOffset = 120;

      if (scrollParent === window) {
        const y =
          element.getBoundingClientRect().top + window.scrollY - headerOffset;
        window.scrollTo({ top: y, behavior: "smooth" });
      } else {
        const parentEl = scrollParent as HTMLElement;
        const parentRect = parentEl.getBoundingClientRect();
        const elemRect = element.getBoundingClientRect();
        const relativeTop =
          elemRect.top - parentRect.top + parentEl.scrollTop - headerOffset + 16;
        parentEl.scrollTo({ top: relativeTop, behavior: "smooth" });
      }

      try {
        if (!(element as HTMLElement).hasAttribute("tabindex")) {
          (element as HTMLElement).setAttribute("tabindex", "-1");
        }
        (element as HTMLElement).focus({ preventScroll: true });
      } catch (e) {
        /* ignore */
      }

      const prevBoxShadow = (element as HTMLElement).style.boxShadow;
      (element as HTMLElement).style.boxShadow =
        "0 0 0 6px rgba(99,102,241,0.12)";
      setTimeout(() => {
        (element as HTMLElement).style.boxShadow = prevBoxShadow;
      }, 1200);
    }
  };

  const fadeInUp: any = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] } },
  };

  const staggerContainer: any = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.12 } },
  };

  const AnswerBlock = ({
    answer,
    qid,
    expandedAnswers,
    setExpandedAnswers,
  }: {
    answer: string;
    qid: number;
    expandedAnswers: Set<number>;
    setExpandedAnswers: React.Dispatch<React.SetStateAction<Set<number>>>;
  }) => {
    const isExpanded = expandedAnswers.has(qid);
    const maxLen = 150;
    const needsTruncate = answer.length > maxLen;
    const display =
      !isExpanded && needsTruncate ? answer.slice(0, maxLen) + "..." : answer;

    const toggle = () => {
      setExpandedAnswers((prev) => {
        const next = new Set(prev);
        if (next.has(qid)) next.delete(qid);
        else next.add(qid);
        return next;
      });
    };

    return (
      <>
        <p className="text-slate-700 text-sm leading-relaxed">{display}</p>
        {needsTruncate && (
          <button
            onClick={toggle}
            className="mt-2 text-indigo-600 text-sm font-medium hover:underline"
          >
            {isExpanded ? "Show less" : "Show more"}
          </button>
        )}
      </>
    );
  };

  return (
    <div
      className="min-h-screen bg-background font-sans text-slate-900"
      style={{
        fontFamily: "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <main className="max-w-7xl mx-auto p-4 md:p-8 pt-20 sm:pt-0 font-sans">
        <div className="sticky top-16 z-40 mb-2 bg-background/0 backdrop-blur-sm">
          <div className="flex items-center justify-between py-1">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={() => navigate("/history")}
              >
                <ArrowLeft className="w-4 h-4 text-slate-600" />
              </Button>
              <div>
                <h1
                  className="text-xl sm:text-3xl md:text-4xl font-bold mb-2"
                  style={{
                    fontFamily:
                      "'Manrope', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                  }}
                >
                  {evaluationData.fileName}
                </h1>
                <p className="text-sm text-muted-foreground">
                  {evaluationData.date} • {evaluationData.duration}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                onClick={() => toast.success("Downloading...")}
                aria-label="Download report"
                title="Download report"
                className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 w-8 sm:h-9 sm:w-auto px-0 sm:px-3 rounded-full sm:rounded-md flex items-center justify-center sm:justify-start gap-2"
              >
                <Download className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                <span className="text-sm hidden sm:inline">Download</span>
              </Button>

              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileSidebarOpen(true)}
                className="h-7 w-7 lg:hidden border border-gray-200 bg-white"
                aria-label="Open Table of Contents"
              >
                <MoreVertical className="w-3 h-3 sm:w-4 sm:h-4 text-slate-700" />
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Sidebar */}
        {mobileSidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/40"
              onClick={() => setMobileSidebarOpen(false)}
            />

            <motion.aside
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="relative w-4/5 max-w-xs h-full bg-white shadow-xl overflow-auto"
            >
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="font-semibold">Table of Contents</h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setMobileSidebarOpen(false)}
                  aria-label="Close sidebar"
                >
                  <X className="w-4 h-4 text-slate-600" />
                </Button>
              </div>

              <div className="p-2">
                <button
                  onClick={() => {
                    scrollToSection("overall");
                    setMobileSidebarOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg text-left ${
                    activeSection === "overall"
                      ? "bg-indigo-600 text-white shadow-md"
                      : "hover:bg-slate-100 text-slate-600"
                  }`}
                >
                  <LayoutDashboard className="w-5 h-5" />
                  <span className="font-bold text-sm">Overall Analysis</span>
                </button>

                <div className="h-px bg-gray-100 my-2" />

                {evaluationData.feedback.map((q, idx) => (
                  <button
                    key={`mobile-${q.id}`}
                    onClick={() => {
                      scrollToSection(`question-${q.id}`);
                      setMobileSidebarOpen(false);
                    }}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-all text-left group ${
                      activeSection === `question-${q.id}`
                        ? "bg-indigo-50 border-indigo-200"
                        : "hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`flex items-center justify-center w-7 h-7 rounded-md text-xs font-bold border ${
                          activeSection === `question-${q.id}`
                            ? "bg-indigo-600 text-white border-indigo-600"
                            : getScoreColor(q.score)
                        }`}
                      >
                        {idx + 1}
                      </span>
                      <span
                        className={`text-sm font-semibold ${
                          activeSection === `question-${q.id}`
                            ? "text-indigo-900"
                            : "text-slate-600"
                        }`}
                      >
                        Question {idx + 1}
                      </span>
                    </div>
                    <ChevronRight
                      className={`w-4 h-4 ${
                        activeSection === `question-${q.id}`
                          ? "text-indigo-600"
                          : "text-slate-300 group-hover:text-slate-400"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </motion.aside>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Sidebar */}
          <div className="hidden lg:block lg:col-span-4 space-y-4 font-sans">
            <div className="sticky top-24 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col max-h-[calc(100vh-120px)] overflow-visible">
              <div className="p-4 bg-slate-50 border-b border-gray-200">
                <h2 className="font-bold text-slate-700 uppercase text-xs tracking-wider">
                  Table of Contents
                </h2>
              </div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={staggerContainer}
                className="overflow-y-auto custom-scrollbar p-2 space-y-1 max-h-[calc(100vh-200px)]"
              >
                <motion.button
                  onClick={() => scrollToSection("overall")}
                  variants={fadeInUp}
                  whileHover={{
                    x: 6,
                    transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                  }}
                  className={`w-full flex items-center gap-3 p-4 rounded-lg transition-all text-left ${
                    activeSection === "overall"
                      ? "bg-indigo-600 text-white shadow-md"
                      : "hover:bg-slate-100 text-slate-600"
                  }`}
                >
                  <LayoutDashboard className="w-5 h-5" />
                  <span className="font-bold text-sm">Overall Analysis</span>
                </motion.button>

                <div className="h-px bg-gray-100 my-2 mx-2" />

                {evaluationData.feedback.map((q, idx) => (
                  <motion.button
                    key={q.id}
                    onClick={() => scrollToSection(`question-${q.id}`)}
                    variants={fadeInUp}
                    whileHover={{
                      x: 6,
                      transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
                    }}
                    className={`w-full flex items-center justify-between p-3 rounded-lg transition-all text-left group ${
                      activeSection === `question-${q.id}`
                        ? "bg-indigo-50 border-indigo-200"
                        : "hover:bg-slate-50"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span
                        className={`flex items-center justify-center w-7 h-7 rounded-md text-xs font-bold border ${
                          activeSection === `question-${q.id}`
                            ? "bg-indigo-600 text-white border-indigo-600"
                            : getScoreColor(q.score)
                        }`}
                      >
                        {idx + 1}
                      </span>
                      <span
                        className={`text-sm md:text-base font-semibold ${
                          activeSection === `question-${q.id}`
                            ? "text-indigo-900"
                            : "text-slate-600"
                        }`}
                      >
                        Question {idx + 1}
                      </span>
                    </div>
                    <ChevronRight
                      className={`w-4 h-4 ${
                        activeSection === `question-${q.id}`
                          ? "text-indigo-600"
                          : "text-slate-300 group-hover:text-slate-400"
                      }`}
                    />
                  </motion.button>
                ))}
              </motion.div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-8 space-y-8 overflow-y-auto max-h-[calc(100vh-160px)] font-sans">
            {/* Overall Section */}
            <div id="overall" className="scroll-mt-24 space-y-6">
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                whileHover={{ y: -6 }}
                className="rounded-lg"
              >
                <Card className="p-8 border-none shadow-lg bg-gradient-to-br from-slate-900 to-slate-800 text-white relative overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-600 rounded-full blur-3xl opacity-20 -translate-y-1/2 translate-x-1/2"></div>

                  <div className="relative z-10">
                    <h2 className="text-2xl font-semibold mb-6">
                      Overall Performance
                    </h2>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                      <div>
                        <p className="text-slate-400 text-sm font-medium uppercase mb-1">
                          Your Score
                        </p>
                        <div className="text-5xl font-black tracking-tight">
                          {percentage.toFixed(0)}%
                        </div>
                      </div>

                      <div className="flex flex-col justify-center">
                        <div className="flex justify-between text-sm border-b border-slate-700 pb-2 mb-2">
                          <span className="text-slate-400">Obtained Marks</span>
                          <span className="font-bold">{totalScore}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-400">Maximum Marks</span>
                          <span className="font-bold">{maxScore}</span>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="bg-white/10 p-3 rounded-lg flex items-center justify-between">
                          <span className="text-sm text-slate-300">Date</span>
                          <span className="font-medium">{evaluationData.date}</span>
                        </div>
                        <div className="bg-white/10 p-3 rounded-lg flex items-center justify-between">
                          <span className="text-sm text-slate-300">Duration</span>
                          <span className="font-medium">
                            {evaluationData.duration}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            </div>

            {/* Questions List */}
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold">Questions & Feedback</h2>
              {evaluationData.feedback.map((q, index) => (
                <motion.div
                  key={q.id}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.35, delay: index * 0.04 }}
                  whileHover={{ y: -6 }}
                  className="rounded-md"
                >
                  <Card
                    id={`question-${q.id}`}
                    tabIndex={-1}
                    className="scroll-mt-24 border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-all bg-white"
                  >
                    <div className="bg-slate-50/80 p-5 border-b border-slate-100 flex justify-between items-start gap-4">
                      <div className="max-w-[calc(100%-6rem)]">
                        <h3 className="text-lg md:text-xl text-slate-800 leading-snug">
                          <span className="text-indigo-600 mr-2 font-semibold">
                            Q{index + 1}.
                          </span>
                          <span className="font-semibold">{q.question}</span>
                        </h3>
                      </div>
                      <span
                        className={`shrink-0 px-3 py-1 rounded-full text-xs font-bold border ${getScoreColor(
                          q.score
                        )}`}
                      >
                        {q.score}/10
                      </span>
                    </div>

                    <div className="p-6 space-y-6">
                      <div className="relative pl-4 border-l-2 border-slate-200">
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                          Your Answer
                        </p>
                        <AnswerBlock
                          answer={q.answer}
                          qid={q.id}
                          expandedAnswers={expandedAnswers}
                          setExpandedAnswers={setExpandedAnswers}
                        />
                      </div>

                      <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-100 flex gap-3 items-start">
                        <div className="bg-white p-1.5 rounded-full shadow-sm mt-0.5">
                          <AlertCircle className="w-4 h-4 text-indigo-600" />
                        </div>
                        <div>
                          <p className="text-xs font-bold text-indigo-900 uppercase mb-1">
                            Analysis
                          </p>
                          <p className="text-sm text-slate-700">{q.feedback}</p>
                        </div>
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Results;

